import { useEffect, useState } from 'react';
import Modal from './Modal';
import { POS_PERMISSIONS, POS_ROLE_NAME_MAX_LENGTH } from '../constants/posConst';
import { validateRole } from '../utils/posClientUtil';
import '../css/PosAdmin.css';

/**
 * Creates or edits one role.
 *
 * Built on the shared Modal, which already carries a × and does NOT close on a
 * backdrop click (CLAUDE.md > Behavior) — half-ticked permissions dismissed by
 * a stray tap is exactly the loss that rule exists to prevent.
 *
 * NOTHING IS WRITTEN HERE. Confirming hands the role back to the form, which
 * saves every role together with the rest of the client. That matches the
 * Staff tab, and it is what lets roles be defined on a client that has not
 * been registered yet — there is no id to write under until it is.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {?{id: string, name: string, permissions: string[]}} props.role - the
 *   role being edited, or null to add a new one.
 * @param {Array<{id: string, name: string, permissions: string[]}>} props.existingRoles
 *   - every role already defined for this client, for the duplicate-name check.
 * @param {(role: {id: string, name: string, permissions: string[]}) => void} props.onSave
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function PosRoleModal({ isOpen, role = null, existingRoles = [], onSave, onClose }) {
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');

  // Re-seeded each time the dialog opens, and on the role it opens on: without
  // this, editing a role straight after adding one would show the previous
  // role's ticks.
  useEffect(() => {
    if (!isOpen) return;
    setName(role?.name || '');
    setPermissions(role?.permissions || []);
    setIsAdmin(role?.isAdmin === true);
    setError('');
  }, [isOpen, role]);

  /**
   * Turns one permission on or off.
   *
   * @param {string} key - a key from POS_PERMISSIONS.
   * @returns {void}
   */
  const toggle = (key) => {
    setPermissions((current) => (current.includes(key)
      ? current.filter((existing) => existing !== key)
      : [...current, key]));
  };

  /**
   * Validates the role and hands it to the form, or shows why it cannot be.
   *
   * @returns {void}
   */
  const handleConfirm = () => {
    try {
      // The id is passed ONLY when editing — that is how validateRole tells an
      // edit from an addition, and so how it knows whether a role with this
      // name is this same role or a clash with another one.
      onSave(validateRole({ id: role?.id, name, permissions, isAdmin }, existingRoles));
    } catch (validationError) {
      setError(validationError.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={role ? 'Edit role' : 'Add Role'}
      type="info"
      hideIcon
      showCancel
      confirmText={role ? 'Save role' : 'Add role'}
      onConfirm={handleConfirm}
    >
      <div className="pa-role-form">
        <label className="pa-field">
          <span className="pa-field-label">Role name</span>
          <input
            className="pa-input"
            value={name}
            onChange={(event) => { setName(event.target.value); setError(''); }}
            // Enter confirms the role. Without this it would submit the client
            // form this dialog is rendered inside — saving the whole client
            // from a half-filled role dialog.
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              handleConfirm();
            }}
            placeholder="Cashier"
            maxLength={POS_ROLE_NAME_MAX_LENGTH}
            autoComplete="off"
          />
        </label>

        {/* The club's own administrator. Everything below is implied, so the
            ticks are hidden rather than left to be read as the limit. */}
        <label className="pa-check">
          <input type="checkbox" checked={isAdmin} onChange={(event) => setIsAdmin(event.target.checked)} />
          <span>
            <span className="pa-field-label">Store admin</span>
            <span className="pa-field-hint">
              Full access at this club&rsquo;s till, can filter sales by staff, and can adjust what
              the club&rsquo;s other roles may do (Config &rarr; Staff Access).
            </span>
          </span>
        </label>

        {!isAdmin && (
          <fieldset className="pa-role-perms">
            <legend className="pa-field-label">Permissions</legend>
            {POS_PERMISSIONS.map(({ key, label }) => (
              <label key={key} className="pa-check pa-role-perm">
                <input
                  type="checkbox"
                  checked={permissions.includes(key)}
                  onChange={() => toggle(key)}
                />
                <span>{label}</span>
              </label>
            ))}
          </fieldset>
        )}

        {!isAdmin && permissions.length === 0 && !error && (
          <p className="pa-field-hint">
            Nothing ticked. The role saves, but it grants no access at all — useful for
            parking someone, easy to create by accident.
          </p>
        )}

        {error && <p className="pa-role-error">{error}</p>}
      </div>
    </Modal>
  );
}

export default PosRoleModal;
